import { type BuyStickerCardProps } from '@athena-lib/BuyStickerCard';
import { formatSize } from '@athena-lib/ProductWizard';
import { getLocalizedInfo } from './localization';
import { checkoutDrawerActions } from './checkoutDrawerContext.svelte';
import type { Figure } from '$lib/server/figures/types';
import type { StickerShopMeta } from '$lib/types/StickerShopMeta';

import { type LocalizationContext } from '$lib/i18n';
import { trackFigureAddToCart, trackFigureItemSelect } from '$lib/gtm/ecommerce-tracking';
import type { TrackFigure } from '$lib/gtm/types';
import type { getCheckoutSdk } from '$lib/checkout.svelte';
import { getText } from '$lib/http';

const getFullStickerUrl = (
  slug: string,
  figure: Figure,
  stickerShopMeta: StickerShopMeta,
): string | undefined => {
  const figureTagId = figure.tag_id.toString();
  const foundFigureTag = [
    ...stickerShopMeta.figureTags.flatMap((x) => [x, ...(x.children ?? [])]),
  ].find((x) => figureTagId === x?.id);
  if (!stickerShopMeta.stickerShopUrls) {
    return undefined;
  }

  return `${stickerShopMeta.stickerShopUrls.baseSlug}/${
    foundFigureTag?.slug ?? figureTagId
  }/${slug}`;
};

const getTrackingData = (data: {
  figure: Figure;
  stickerShopMeta: StickerShopMeta;
  localization: LocalizationContext;
}): TrackFigure => ({
  figure: data.figure,
  stickerShopMeta: data.stickerShopMeta,
  localization: data.localization,
});

export const getBuyStickerCardProps = (
  figure: Figure,
  stickerShopMeta: StickerShopMeta,
  locale: LocalizationContext,
  refreshCart: () => Promise<number>,
  checkoutSdk: ReturnType<typeof getCheckoutSdk> | null,
): BuyStickerCardProps => {
  const localizedInfo = getLocalizedInfo(figure, locale);

  const trackingData = getTrackingData({
    figure,
    stickerShopMeta,
    localization: locale,
  });
  const image = {
    alt: localizedInfo.alt_text?.trim() || localizedInfo.text,
    src: figure.image_url,
  };
  const formattedSize = formatSize(
    figure.product_item.attributes.width_mm,
    figure.product_item.attributes.height_mm,
    locale,
    true,
  );
  const stickerName = localizedInfo.title ?? '';
  return {
    formattedSize,
    image: image,
    price: localizedInfo.price?.totalFormatted,
    sticker: {
      name: stickerName,
      url: localizedInfo.slug
        ? getFullStickerUrl(localizedInfo.slug, figure, stickerShopMeta)
        : undefined,
    },
    onBuyClick: () => {
      const addItemPromise = checkoutSdk
        ? checkoutSdk.actions.addStickerShopLineItem(figure.id, 1).then((x) => {
            if (!x.success) {
              throw new Error('failed adding sticker item');
            }
          })
        : getText(`/editor/add_sticker_library_item_to_cart/${figure.id}`, {
            redirect: 'manual',
          }).then(() => {});
      return addItemPromise
        .then(() => {
          if (checkoutSdk === null) {
            trackFigureAddToCart(trackingData);
          }
          return refreshCart();
        })
        .then(() => {
          checkoutDrawerActions.addItemToCart({
            image: { src: image.src, alt: image.alt ?? '' },
            formattedSize,
            name: stickerName,
          });
        });
    },
    onStickerClick: () => trackFigureItemSelect(trackingData),
  };
};
